import React from 'react';
import { 
  UserIcon, 
  PhoneIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { Patient } from '../../types/patient';
import { AlertBadge, HealthMetric } from './shared';
import { ageInYears, formatDate, formatDateOnly } from '../../utils/dateFormat';
import { useMedicalAlerts } from '../../hooks/usePatientCockpit';
import { patientIdLabel, patientName } from '../../utils/patientDisplay';
import { contactDisplay } from '../../utils/patientContact';
import { useOrganizationStore } from '../../stores/organizationStore';

/**
 * HeroSection - Critical patient information display at the top of the cockpit
 * Shows the most important clinical data that doctors need immediately
 */
interface HeroSectionProps {
  patient: Patient;
  className?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ 
  patient, 
  className = '' 
}) => {
  const { operationMode } = useOrganizationStore();
  const { alerts, criticalCount } = useMedicalAlerts(patient);
  const dob = patient.demographics.dateOfBirth;
  const age = ageInYears(dob);

  // Get primary emergency contact
  const primaryContact = patient.emergencyContacts?.find(contact => contact.isPrimary) || patient.emergencyContacts?.[0];
  const phone = contactDisplay(patient.contact?.phone, patient.contact?.phoneUnreadable);
  const email = contactDisplay(patient.contact?.email, patient.contact?.emailUnreadable);
  const primaryContactPhone = primaryContact && contactDisplay(primaryContact.phone, primaryContact.phoneUnreadable);

  return (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6 ${className}`}>
      {/* Patient Name and Basic Info */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
            <UserIcon className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">
              {patientName(patient, 'Patient')}
            </h1>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>{patientIdLabel(patient, operationMode)}</span>
              {age !== null && (
                <span>DOB: {formatDateOnly(dob)} · Age: {age}</span>
              )}
              {patient.demographics.gender && (
                <span className="capitalize">{patient.demographics.gender}</span>
              )}
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                patient.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {patient.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {(phone || email) && (
              <div className="flex flex-wrap items-center gap-x-4 mt-1 text-sm text-gray-600">
                {phone && <span>Phone: {phone}</span>}
                {email && <span>Email: {email}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Emergency Contact Quick Access */}
        {primaryContact && (
          <div className="text-right">
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <PhoneIcon className="w-4 h-4 mr-1" />
              Emergency Contact
            </div>
            <div className="text-sm font-medium text-gray-900">
              {primaryContact.firstName} {primaryContact.lastName}
            </div>
            <div className="text-xs text-gray-500 capitalize">
              {primaryContact.relationship}
            </div>
            {primaryContactPhone && (
              <div className="text-xs text-gray-600">
                {primaryContactPhone}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Critical Alerts */}
      {alerts.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center mb-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              Medical Alerts {criticalCount > 0 && `(${criticalCount} Critical)`}
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {alerts.map((alert, index) => (
              <div 
                key={index}
                className={`p-3 rounded-lg border-l-4 ${
                  alert.type === 'critical' 
                    ? 'bg-red-50 border-red-400 border border-red-200' 
                    : alert.type === 'warning'
                    ? 'bg-yellow-50 border-yellow-400 border border-yellow-200'
                    : 'bg-blue-50 border-blue-400 border border-blue-200'
                }`}
              >
                <div className="flex items-start">
                  <span className="text-lg mr-2" role="img" aria-hidden="true">
                    {alert.icon}
                  </span>
                  <div>
                    <AlertBadge type={alert.type} size="sm">
                      {alert.category}
                    </AlertBadge>
                    <p className="text-sm text-gray-700 mt-1">
                      {alert.message}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Medical Summary Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Allergies */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center mb-2">
            <span className="text-red-500 mr-2" role="img" aria-hidden="true">⚠️</span>
            <h4 className="text-sm font-medium text-gray-900">Allergies</h4>
          </div>
          {patient.medicalRecord.allergies && patient.medicalRecord.allergies.length > 0 ? (
            <div className="space-y-1">
              {patient.medicalRecord.allergies.slice(0, 3).map((allergy, index) => (
                <div key={index} className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded">
                  {allergy}
                </div>
              ))}
              {patient.medicalRecord.allergies.length > 3 && (
                <div className="text-xs text-gray-500">
                  +{patient.medicalRecord.allergies.length - 3} more
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500">Allergies not recorded</div>
          )}
        </div>

        {/* Current Medications */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center mb-2">
            <span className="text-green-500 mr-2" role="img" aria-hidden="true">💊</span>
            <h4 className="text-sm font-medium text-gray-900">Medications</h4>
          </div>
          {patient.medicalRecord.medications && patient.medicalRecord.medications.length > 0 ? (
            <div className="space-y-1">
              {patient.medicalRecord.medications.slice(0, 3).map((medication, index) => (
                <div key={index} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                  {medication}
                </div>
              ))}
              {patient.medicalRecord.medications.length > 3 && (
                <div className="text-xs text-gray-500">
                  +{patient.medicalRecord.medications.length - 3} more
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500">Medications not recorded</div>
          )}
        </div>

        {/* Medical Conditions */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center mb-2">
            <span className="text-blue-500 mr-2" role="img" aria-hidden="true">🏥</span>
            <h4 className="text-sm font-medium text-gray-900">Conditions</h4>
          </div>
          {patient.medicalRecord.conditions && patient.medicalRecord.conditions.length > 0 ? (
            <div className="space-y-1">
              {patient.medicalRecord.conditions.slice(0, 3).map((condition, index) => (
                <div key={index} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                  {condition}
                </div>
              ))}
              {patient.medicalRecord.conditions.length > 3 && (
                <div className="text-xs text-gray-500">
                  +{patient.medicalRecord.conditions.length - 3} more
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500">Conditions not recorded</div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center mb-2">
            <InformationCircleIcon className="w-4 h-4 text-gray-500 mr-2" />
            <h4 className="text-sm font-medium text-gray-900">Quick Info</h4>
          </div>
          <div className="space-y-2">
            {patient.demographics.language && (
              <div className="text-xs">
                <span className="text-gray-500">Language:</span>
                <span className="ml-1 font-medium text-gray-700 uppercase">
                  {patient.demographics.language}
                </span>
              </div>
            )}
            {patient.medicalRecord.mrn && (
              <div className="text-xs">
                <span className="text-gray-500">MRN:</span>
                <span className="ml-1 font-medium text-gray-700">
                  {patient.medicalRecord.mrn}
                </span>
              </div>
            )}
            <div className="text-xs">
              <span className="text-gray-500">Created:</span>
              <span className="ml-1 font-medium text-gray-700">
                {formatDate(patient.createdAt)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;